import { useState } from 'react';
import { Client, ClientFormData } from '../types';
import { adminAPI } from '../services/api/adminAPI';
import { useNotifications } from '../providers/NotificationProvider';

export const useClients = () => {
	const [clients, setClients] = useState<Client[]>([]);
	const [loading, setLoading] = useState(false);
	const { addNotification } = useNotifications();

	const loadClients = async (filters?: { search?: string; status?: string; healthStatus?: string }) => {
		setLoading(true);
		try {
			const response = await adminAPI.getClients({ ...filters, limit: 100 });
			setClients(response.clients);
		} catch (error) {
			addNotification({
				type: 'error',
				title: 'Failed to Load Clients',
				message: error instanceof Error ? error.message : 'Unknown error occurred',
			});
		} finally {
			setLoading(false);
		}
	};

	const createClient = async (clientData: ClientFormData) => {
		try {
			await adminAPI.createClient(clientData);
			addNotification({
				type: 'success',
				title: 'Client Created',
				message: 'Client has been successfully created',
			});
			loadClients();
		} catch (error) {
			addNotification({
				type: 'error',
				title: 'Failed to Create Client',
				message: error instanceof Error ? error.message : 'Unknown error occurred',
			});
			throw error;
		}
	};

	const updateClient = async (clientId: string, updates: Partial<ClientFormData>) => {
		try {
			await adminAPI.updateClient(clientId, updates);
			addNotification({
				type: 'success',
				title: 'Client Updated',
				message: 'Client has been successfully updated',
			});
			loadClients();
		} catch (error) {
			addNotification({
				type: 'error',
				title: 'Failed to Update Client',
				message: error instanceof Error ? error.message : 'Unknown error occurred',
			});
			throw error;
		}
	};

	const deleteClient = async (clientId: string) => {
		try {
			await adminAPI.deactivateClient(clientId);
			addNotification({
				type: 'success',
				title: 'Client Deactivated',
				message: 'Client has been successfully deactivated',
			});
			loadClients();
		} catch (error) {
			addNotification({
				type: 'error',
				title: 'Failed to Deactivate Client',
				message: error instanceof Error ? error.message : 'Unknown error occurred',
			});
			throw error;
		}
	};

	return {
		clients,
		loading,
		loadClients,
		createClient,
		updateClient,
		deleteClient,
	};
};
